<template>
  <div class="animate-fade-in-right">
    <!-- Header -->
    <header class="mb-10 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
      <div>
        <div class="flex items-center gap-4 mb-2">
          <button @click="$router.push({ name: 'events' })"
            class="bg-white/10 hover:bg-white/20 p-2 rounded-xl text-white transition-colors backdrop-blur-md border border-white/10">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 class="text-4xl font-bold text-white tracking-tight drop-shadow-sm">
            {{ eventById[0]?.eventName || t('Loading...') }}
          </h2>
        </div>
        <div class="flex items-center gap-2 text-white/70 ml-14 font-medium">
          <span class="bg-indigo-500/20 text-indigo-300 px-3 py-1 rounded-full text-xs uppercase tracking-wider border border-indigo-500/30">{{ t('Guest List') }}</span>
          <span>•</span>
          <span>{{ guests.length }} {{ t('Total Guests') }}</span>
        </div>
      </div>

      <button @click="handlePopOpenform('add')"
        class="group relative overflow-hidden bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold py-3 px-8 rounded-2xl shadow-[0_4px_20px_rgba(99,102,241,0.4)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(99,102,241,0.5)]">
        <span class="flex items-center gap-2 relative z-10">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ t('Add New Guest') }}
        </span>
      </button>
    </header>

    <!-- Summary Cards -->
    <div v-if="currencyTotals.length > 0" class="mb-10 flex flex-wrap justify-center gap-4">
      <div v-for="item in currencyTotals" :key="item.currency"
        class="relative min-w-[180px] bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-6 shadow-[0_8px_32px_rgba(0,0,0,0.2)] group hover:bg-white/15 transition-all duration-500 hover:-translate-y-1">
        
        <div class="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        
        <div class="flex flex-col items-center text-center relative z-10">
          <div class="flex items-center gap-2 mb-2">
            <div class="w-2 h-2 rounded-full bg-emerald-400"></div>
            <span class="text-white/40 text-[10px] font-black uppercase tracking-[0.2em]">{{ t('Total') }} {{ item.currency }}</span>
          </div>
          <div class="text-3xl font-black text-white tracking-tighter">
            {{ numeralFormat(item.total, '0,0[.]00') }}
          </div>
          <div class="mt-3 w-10 h-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 opacity-50 group-hover:w-16 transition-all duration-500"></div>
        </div>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="mb-10 w-full max-w-xl">
      <div class="relative group">
        <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <svg class="w-5 h-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input v-model="searchQuery" type="text" :placeholder="t('Search guests by name...')"
          class="w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-indigo-200/50 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-white/10 transition-all font-medium" />
      </div>
    </div>

    <!-- Guest Grid -->
    <div v-if="filteredGuests.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="guest in filteredGuests" :key="guest.id"
        class="group bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/20 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.2)]">
        
        <div class="flex justify-between items-start mb-6">
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center text-white font-bold text-xl shadow-inner group-hover:scale-110 transition-transform">
              {{ guest.guestName.charAt(0) }}
            </div>
            <div>
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-xl font-bold text-white group-hover:text-indigo-200 transition-colors">
                  {{ guest.guestName }}
                </h3>
                <span v-if="guest.type && guest.type !== 'none'" 
                  class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border"
                  :class="typeStyles[guest.type] || typeStyles.none">
                  {{ guest.type }}
                </span>
              </div>
              <p class="text-indigo-300/80 text-sm font-medium">{{ t('Guest') }}</p>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <!-- Edit Button -->
            <button @click.stop="handlePopOpenform('edit', guest)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 text-indigo-300 hover:text-white transition-all border border-transparent hover:border-white/20"
              :title="t('Edit Guest')">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>

            <!-- Delete Button -->
            <button @click.stop="handlePopDelete(guest)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-red-500/20 text-red-300 hover:text-red-200 transition-all border border-transparent hover:border-red-500/30"
              :title="t('Delete Guest')">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        <div class="space-y-4">
          <div class="bg-black/20 rounded-2xl p-4 border border-white/5">
            <div class="text-white/40 text-xs uppercase tracking-wider mb-1">{{ t('Amount Given') }}</div>
            <div class="text-2xl font-black text-emerald-400">
              {{ numeralFormat(guest.amount, '0,0[.]00') }} <span class="text-sm font-bold text-white/60 ml-1">{{ guest.currency }}</span>
            </div>
          </div>

          <div v-if="guest.description" class="text-white/60 text-sm leading-relaxed min-h-[3rem] italic">
            "{{ guest.description }}"
          </div>
          <div v-else class="text-white/20 text-sm italic">
            {{ t('No description provided') }}
          </div>
        </div>
        

        <div class="mt-6 pt-4 border-t border-white/10 flex justify-between items-center">
          <div class="flex items-center gap-2 text-white/40 text-xs">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ formatDate2(guest.createdAt) }}</span>
          </div>
          <span class="text-indigo-300/60 text-xs font-bold uppercase tracking-widest">{{ t('Details') }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center py-20 bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 text-white/30">
      <div class="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-4">
        <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      </div>
      <p class="text-xl font-medium">{{ t('No guests found') }}</p>
      <p class="text-sm">{{ t('Try adjusting your search or add a new guest.') }}</p>
    </div>
  </div>
  <component :is="currentComponent" :visible="openForm" :isEditDoc="isEditDoc" :doc="docData" :isFailed="isFailed"
    :elementName="elementName" :docId="deleteId" :elementCategory="elementCategory" :informMessage="informMessage"
    :showToast="showToast"  @onClose="handleListenEmitEvent" />
</template>

<script setup>
import DeleteConfirmation from '@/components/DeleteConfirmation.vue';
import { ref, defineProps, onMounted, computed ,watch} from 'vue';

import { getCollectionQuery } from '@/composables/getCollection';
import { where, orderBy, documentId } from 'firebase/firestore';
import GuestForm from '@/components/Modal/GuestForm.vue';
import ToastedVue from '@/components/ToastedVue.vue';
import { projectAuth } from '@/firebase/config';
import { formatDate, formatDate2 } from '@/composables/formatDate';
import { calculateExpirePlan } from '@/composables/calculateExpirePlan';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  id: {
    type: String,
    required: true
  }
});

const searchQuery = ref("");
const dateFilter = ref("");

const openForm = ref(false);
const isEditDoc = ref(false);
const informMessage = ref("");
const showToast = ref(false);
const isFailed = ref(false);
const currentComponent = ref("");

const isExpired = ref(false);

const docData = ref(null);
const elementCategory = ref(null);
const elementName = ref(null);

const deleteId = ref(null);
const eventById = ref([]);


const guests = ref([]);

const typeStyles = {
  groom: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  bride: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  both: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  none: 'bg-white/5 text-white/40 border-white/10'
};

const userByID = ref([]);
const fetchUserById = async () => {
  try {
    await getCollectionQuery(
      "users",
      [where(documentId(), "==", projectAuth.currentUser?.uid)],
      (data) => {
        userByID.value = data;
             if(userByID.value.length > 0){
          fetchPlanById();
        }
       
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

const planByID = ref([]);
const fetchPlanById = async () => {
  try {
    await getCollectionQuery(
      "plans",
      [where(documentId(), "==", userByID.value[0]?.planId)],
      (data) => {
        planByID.value = data;

      
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};


const fetchEventById=async()=>{
  try {
    await getCollectionQuery(
      "events",
      [where(documentId(), "==", props.id)],
      (data) => {
        eventById.value = data;

      


      },
      null,
      true
    );


  } catch (error) {
    console.log("failed to fetch event", error);
  }
}


watch(
  [userByID, planByID],
  ([newUser, newPlan]) => {
    if (newUser?.length > 0 && newPlan?.length > 0) {
      const user = newUser[0];
      const plan = newPlan[0];

      if (
        user.subscriptionStart &&
        plan.planDuration &&
        plan.planType
      ) {
        const res = calculateExpirePlan(
          user.subscriptionEnd,
          plan.planType
        );
        isExpired.value = res;

   
      } else {
        console.log("Waiting for subscription data...", {
          hasStart: !!user?.subscriptionStart,
          hasDuration: !!plan?.planDuration,
          hasType: !!plan?.planType
        });
      }
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

const fetchGuests = async () => {
  try {
    await getCollectionQuery(
      "guests",
      [where("eventId", "==", props.id), orderBy("createdAt", "desc")],
      (data) => {
        guests.value = data;

   


      },
      null,
      true
    );


  } catch (error) {
    console.log("failed to fetch event", error);
  }

}

const filteredGuests = computed(() => {
  return guests.value.filter((guest) => {
    // 1. Name Filter
    const matchesName = guest.guestName
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());

   
    let matchesDate = true;
    if (dateFilter.value) {
      const createdAt = new Date(guest.createdAt).toDateString();
  
    
      matchesDate = createdAt === dateFilter.value;
    }

    return matchesName && matchesDate;
  });
});

const currencyTotals = computed(() => {
  const totals = {};
  guests.value.forEach((guest) => {
    const cur = guest.currency || 'USD';
    const amt = parseFloat(guest.amount) || 0;
    if (!totals[cur]) {
      totals[cur] = 0;
    }
    totals[cur] += amt;
  });
  return Object.entries(totals).map(([currency, total]) => ({ currency, total }));
});
onMounted(async() => {
 await fetchUserById();
  
  fetchGuests();
  fetchEventById();
})

const handlePopOpenform = (status, item) => {
  if (status == "add") {
       console.log("max guest reached", planByID.value[0]?.maxGuest);
     if (planByID.value[0]?.maxGuest === guests.value.length) {

   
   
      isFailed.value = true;
      informMessage.value =
        t("You have reached the maximum number of guests allowed in your plan!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    if (isExpired.value) {
      isFailed.value = true;
      informMessage.value =
        t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    } else {
      openForm.value = true;
      currentComponent.value = GuestForm;
    }
    
  } else {
    if (isExpired.value) {
      isFailed.value = true;
      informMessage.value =
        t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    isEditDoc.value = true;
    docData.value = item;
    openForm.value = true;
    currentComponent.value = GuestForm;
  }
};

const handlePopDelete = (item) => {
  if(isExpired.value){
    isFailed.value = true;
    informMessage.value =
      t("Your subscription has expired!");
    showToast.value = true;
    currentComponent.value = ToastedVue;
    return;
  }
  deleteId.value = item.id;
  elementCategory.value = "guests";
  elementName.value = item.guestName;
  showToast.value = true;
  currentComponent.value = DeleteConfirmation
};




const handleListenEmitEvent = (status) => {
  if (status == "add" || status == "create") {
    informMessage.value = t("Data has been created successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = ToastedVue
    showToast.value = true;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    
  } else if (status == "update") {
    informMessage.value = t("Data has been updated successfully !");
    searchQuery.value = "";

    currentComponent.value = ToastedVue;
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
   
  } else if (status == "close") {
    console.log("part close");
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
  
  } else if (status == "delete") {
    informMessage.value = t("Data has been deleted successfully !");

    showToast.value = true;
    currentComponent.value = ToastedVue;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
  
  } else if (status == "copy") {
    informMessage.value = t("Data has been copied successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
  
  } else {
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    currentComponent.value = "";
    elementCategory.value = null;
    elementName.value = null;
    
  }
};


</script>

<style scoped>
.animate-fade-in-right {
  animation: fadeInRight 0.5s ease-out forwards;
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>